@if(neighborhoodService.currentNeighborhood) {
  <div class="container-fluid pt-4">
    <div class="row">

      <!--
        El proyecto no tiene guards de ruta: la protección por perfil se hace
        únicamente con este @if, igual que las secciones sudo-only ya existentes
        (general-page, settings-passes-page, settings-payments-page).
        Un usuario que no sea sudo simplemente no ve nada; no se muestra
        pantalla de "no autorizado" para no romper esa consistencia.
      -->
      @if(authService.currentUser?.profile === 'sudo') {

        <div class="col-12 text-secondary mb-1">
          <small><b>
            Dirección del conjunto
          </b></small>
        </div>

        <div class="col-12 bg-white px-3 rounded mb-4 py-3">
          <div class="row">

            <!--
              ORDEN DE LOS CAMPOS: Calle · Código postal · Ciudad · Estado.
              El código postal va ANTES de Ciudad y Estado porque es el que los
              rellena: si fuera al final, el usuario vería cambiar dos campos
              que ya dejó atrás y el flujo no se entendería.
            -->

            <!--
              Los maxlength no son decorativos: el request del API declara
              max_length=45 para street, city y state. Pasarse devuelve un 422.
            -->
            <div class="col-12 col-md-6 mb-3">
              <label for="addressStreet" class="form-label">
                Calle
              </label>
              <input
                id="addressStreet"
                type="text"
                class="form-control"
                maxlength="45"
                autocomplete="address-line1"
                [class.is-invalid]="submitted && errors['street'] !== ''"
                (input)="clearError('street')"
                (keyup.enter)="save()"
                [(ngModel)]="neighborhoodService.currentNeighborhood!.street"
                placeholder="Calle">
              @if(submitted && errors['street'] !== '') {
                <div class="invalid-feedback" role="alert">
                  {{ errors['street'] }}
                </div>
              }
            </div>

            <!--
              zipCode: la columna en base de datos es String(6), pero el request
              del API solo acepta max_length=5. Se respeta el límite del API,
              que es el que rechaza con 422.
              Va como type="text" (no number) para no perder el cero inicial de
              códigos como 01000; inputmode="numeric" levanta el teclado
              numérico en móvil sin cambiar el tipo del control.

              El evento (input) dispara la consulta AUTOMÁTICA a Copomex: solo
              escritura real del usuario. Al cargar la página no se consulta
              nada (ver zipCodeTyped en el .ts). El botón "Buscar" de al lado es
              el disparador EXPLÍCITO y se salta el debounce y el
              distinctUntilChanged (ver zipCodeSearchRequested en el .ts).
            -->
            <div class="col-12 col-md-6 mb-3">
              <label for="addressZipCode" class="form-label">
                Código postal
              </label>

              <!--
                .input-group resuelve la adyacencia del campo y el botón.

                ⚠️ EL .invalid-feedback VA DENTRO DEL GRUPO ⚠️
                Bootstrap solo lo muestra con el selector HERMANO
                `.is-invalid ~ .invalid-feedback`
                (bootstrap.css:2909). Si el mensaje se quedara fuera del
                .input-group dejaría de ser hermano del input y no se vería
                nunca, ni el de campo obligatorio ni el de los 5 dígitos.

                .has-validation va con [class.] y no fijo: es la clase que le
                dice a Bootstrap que el ÚLTIMO hijo del grupo es el mensaje y
                no un control, para calcular los redondeos
                (bootstrap.css:2777). Con el mensaje en pantalla, el input es
                :nth-last-child(3) y pierde el redondeo derecho, y el botón,
                que es :nth-last-child(2), lo conserva. Si la clase fuera fija,
                al no renderizarse el mensaje el input recuperaría su redondeo
                derecho y aparecería una costura redondeada a media caja.
              -->
              <div
                class="input-group"
                [class.has-validation]="zipCodeErrorVisible">
                <input
                  id="addressZipCode"
                  type="text"
                  class="form-control"
                  maxlength="5"
                  inputmode="numeric"
                  autocomplete="postal-code"
                  aria-describedby="addressZipCodeStatus"
                  [class.is-invalid]="zipCodeErrorVisible"
                  (input)="onZipCodeInput(zipCodeInput.value)"
                  (keyup.enter)="save()"
                  [(ngModel)]="neighborhoodService.currentNeighborhood!.zipCode"
                  placeholder="Código postal"
                  #zipCodeInput>

                <!--
                  type="button" explícito: aquí no hay <form>, pero el mismo
                  botón vive dentro de uno en neighborhood-modal, y un <button>
                  sin type es de tipo submit por defecto. Se escribe igual en
                  los dos sitios para que copiar el bloque no reintroduzca el
                  bug.

                  Solo se deshabilita mientras la consulta está en vuelo, para
                  evitar el doble disparo: ahí el motivo es evidente porque el
                  propio botón muestra el spinner. Con menos de 5 dígitos NO se
                  deshabilita —mismo criterio que el botón Guardar—: se pulsa y
                  se pinta el error de formato.

                  Va inmediatamente después del input en el DOM, así que entra
                  en el orden natural de tabulación justo detrás del campo.
                -->
                <button
                  type="button"
                  class="btn btn-outline-primary d-inline-flex align-items-center"
                  [disabled]="searchingZipCode"
                  (click)="searchZipCode()">
                  @if(searchingZipCode) {
                    <span
                      class="spinner-border spinner-border-sm me-2"
                      aria-hidden="true">
                    </span>
                  } @else {
                    <i class="material-icons me-2" aria-hidden="true">search</i>
                  }
                  Buscar
                </button>

                @if(zipCodeErrorVisible) {
                  <div class="invalid-feedback" role="alert">
                    {{ errors['zipCode'] }}
                  </div>
                }
              </div>

              <!--
                Franja de estado de la búsqueda por código postal.
                El contenedor se renderiza SIEMPRE y tiene altura mínima
                reservada en el .scss: así el spinner y los mensajes aparecen y
                desaparecen sin empujar el resto del formulario hacia abajo.
              -->
              <div
                id="addressZipCodeStatus"
                class="zip-code-status">

                @if(searchingZipCode) {
                  <span class="text-secondary d-inline-flex align-items-center" role="status">
                    <span
                      class="spinner-border spinner-border-sm me-2"
                      aria-hidden="true">
                    </span>
                    <small>Buscando…</small>
                  </span>
                }

                @if(zipCodeNotFound) {
                  <small class="text-danger" role="alert">
                    No encontramos ese código postal.
                  </small>
                }

                @if(zipCodeSearchFailed) {
                  <small class="text-danger" role="alert">
                    No pudimos consultar el código postal.
                  </small>
                }

              </div>
            </div>

            <!--
              Ciudad y Estado se autorrellenan desde el código postal, pero
              siguen siendo EDITABLES: los datos históricos no siempre coinciden
              con SEPOMEX y el usuario tiene que poder corregirlos.
            -->
            <div class="col-12 col-md-6 mb-3">
              <label for="addressCity" class="form-label">
                Ciudad
              </label>
              <input
                id="addressCity"
                type="text"
                class="form-control"
                maxlength="45"
                autocomplete="address-level2"
                [class.is-invalid]="submitted && errors['city'] !== ''"
                (input)="clearError('city')"
                (keyup.enter)="save()"
                [(ngModel)]="neighborhoodService.currentNeighborhood!.city"
                placeholder="Ciudad">
              @if(submitted && errors['city'] !== '') {
                <div class="invalid-feedback" role="alert">
                  {{ errors['city'] }}
                </div>
              }
            </div>

            <div class="col-12 col-md-6 mb-3">
              <label for="addressState" class="form-label">
                Estado
              </label>
              <input
                id="addressState"
                type="text"
                class="form-control"
                maxlength="45"
                autocomplete="address-level1"
                [class.is-invalid]="submitted && errors['state'] !== ''"
                (input)="clearError('state')"
                (keyup.enter)="save()"
                [(ngModel)]="neighborhoodService.currentNeighborhood!.state"
                placeholder="Estado">
              @if(submitted && errors['state'] !== '') {
                <div class="invalid-feedback" role="alert">
                  {{ errors['state'] }}
                </div>
              }
            </div>

            <!--
              El botón nunca se deshabilita: si se deshabilitara, el usuario no
              tendría forma de saber qué campo le falta. Al pulsarlo se pinta el
              error junto al campo culpable.
            -->
            <div class="col-12">
              <button
                type="button"
                class="btn btn-success w-100 d-flex align-items-center justify-content-center"
                (click)="save()">
                <i class="material-icons me-2">save</i>
                Guardar
              </button>
            </div>

          </div>
        </div>

      }

    </div>
  </div>
}
